import useRevokeBooksFromSubjectGroups from "api/BookResource/useRevokeBooksFromSubjectGroups";
import useBookAssignees from "api/BookResource/useBookAssignees";
import { useFormik } from "formik";
import { Column } from "primereact/column";
import { DataTable } from "components/PortalDataTable";
import Dialog from "components/Overlays/ResponsiveDialog";
import { useState, useEffect, useMemo } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BsSearch, BsCheckCircle, BsTrash } from "react-icons/bs";
import {
	FiArrowRight,
	FiX,
	FiUsers,
	FiCheckCircle,
	FiAlertTriangle,
	FiLayers,
	FiArrowLeft,
} from "react-icons/fi";
import StepIndicator from "components/StepIndicator";

const RevokeSubjectGroups = ({ book, className, children }) => {
	const [display, setDisplay] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);

	const steps = [
		{ label: "Select Groups", icon: <FiLayers /> },
		{ label: "Confirm Revocation", icon: <FiAlertTriangle /> },
	];

	const [searchTerm, setSearchTerm] = useState("");
	const [reason, setReason] = useState("");
	const [partial, setPartial] = useState(false);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [memberSearchTerm, setMemberSearchTerm] = useState("");
	const [selectedGroups, setSelectedGroups] = useState([]);
	const [revokeResult, setRevokeResult] = useState(null);

	const { data: assignees, isLoading: isLoadingSubjectGroups } = useBookAssignees(
		book?.id,
		display,
	);

	useEffect(() => {
		if (display) {
			setSelectedGroups([]);
			setSelectedMembers([]);
			setSearchTerm("");
			setMemberSearchTerm("");
			setReason("");
			setPartial(false);
			setRevokeResult(null);
			setCurrentStep(0);
		}
	}, [display]);

	const subjectGroups = assignees?.subjectGroups || [];

	const tableData = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return subjectGroups
			.map((subjectGroup) => {
				const assignedUsers = subjectGroup.assignedUsers || [];
				const revokedCount = assignedUsers.filter((member) => member.isRevoked).length;
				const activeCount = assignedUsers.length - revokedCount;

				return {
					id: subjectGroup.id,
					name: subjectGroup.name,
					created_by:
						(subjectGroup?.user?.first_name || "") +
						" " +
						(subjectGroup?.user?.last_name || ""),
					institution_name: subjectGroup?.user?.institutions?.[0]?.name,
					assigned_users_count: subjectGroup.assigned_users_count,
					assignedUsers,
					revokedCount,
					activeCount,
				};
			})
			.filter((group) => !term || (group.name || "").toLowerCase().includes(term));
	}, [subjectGroups, searchTerm]);

	const selectedRevokedMembers = useMemo(() => {
		const byId = new Map();
		selectedGroups.forEach((group) => {
			(group.assignedUsers || []).forEach((member) => {
				if (member.isRevoked && !byId.has(member.id)) {
					byId.set(member.id, member);
				}
			});
		});
		return Array.from(byId.values());
	}, [selectedGroups]);

	// Members available for partial revoke = union of non-revoked members across selected groups.
	const groupMembers = useMemo(() => {
		const byId = new Map();
		selectedGroups.forEach((group) => {
			(group.assignedUsers || []).forEach((member) => {
				if (!member.isRevoked && !byId.has(member.id)) {
					byId.set(member.id, member);
				}
			});
		});
		const all = Array.from(byId.values());
		const term = memberSearchTerm.trim().toLowerCase();
		if (!term) {
			return all;
		}
		return all.filter((member) => {
			const haystack = `${member.first_name || ""} ${member.last_name || ""} ${
				member.email || ""
			}`.toLowerCase();
			return haystack.includes(term);
		});
	}, [selectedGroups, memberSearchTerm]);

	const { mutateAsync, isLoading: isRevoking } = useRevokeBooksFromSubjectGroups();

	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			subjectGroups: [...selectedGroups],
		},
		onSubmit: async () => {
			setCurrentStep(1);
		},

		enableReinitialize: true,
	});

	const handleConfirmSubmit = async () => {
		const subjectGroups = values.subjectGroups.map((group) => group.id);
		const payload = {
			subjectGroups,
			books: [book.id],
			users: partial ? selectedMembers.map((m) => m.id) : undefined,
			reason: reason || undefined,
		};
		const result = await mutateAsync(payload);
		setRevokeResult(result?.data || null);
		setCurrentStep(2);
	};

	const uniqueMemberCount = useMemo(() => {
		const memberIds = new Set();
		selectedGroups.forEach((group) => {
			(group.assignedUsers || []).forEach((member) => {
				if (!member.isRevoked) {
					memberIds.add(member.id);
				}
			});
		});
		return memberIds.size;
	}, [selectedGroups]);

	const formatMemberName = (member) =>
		`${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email || "Student";

	const successCount = revokeResult?.successes?.length ?? 0;
	const failureCount = revokeResult?.failures?.length ?? 0;

	return (
		<>
			<button
				type="button"
				className={`appearance-none border-0 ${className} `}
				onClick={() => setDisplay(true)}
			>
				{children || <BsTrash size={15} />}
			</button>

			<Dialog
				visible={display}
				onHide={() => setDisplay(false)}
				breakpoints={{
					"960px": "90vw",
					"0px": "100vw",
				}}
				className="font-sans"
				style={{ maxWidth: "900px" }}
				showHeader={false}
				contentClassName="rounded-md relative"
				blockScroll={false}
			>
				<button
					type="button"
					onClick={() => setDisplay(false)}
					className="absolute top-4 right-4 hover:bg-gray-100 rounded-full p-2 transition-colors z-10"
				>
					<FiX size={24} />
				</button>

				<div className="pt-8 pb-6">
					<div className="px-6 pb-4 border-b border-gray-200">
						<h3 className="font-cera-black text-red-700 text-2xl mb-2">
							Revoke Book from Subject Groups
						</h3>
						<p className="text-gray-600 text-sm">
							{currentStep === 0 &&
								"Revoke this book from whole subject groups, or only from specific members."}
							{currentStep === 1 && "Review the revocation details and confirm"}
							{currentStep === 2 && "Revocation completed successfully"}
						</p>
						<StepIndicator steps={steps} currentStep={currentStep} />
					</div>

					{currentStep === 0 && (
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-6 py-4">
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-blue-700 mb-1">
									<FiCheckCircle size={18} />
									<span className="text-sm font-medium">Book</span>
								</div>
								<p className="text-base font-bold text-blue-900 truncate">
									{book?.title}
								</p>
							</div>
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-red-700 mb-1">
									<FiLayers size={18} />
									<span className="text-sm font-medium">Groups Selected</span>
								</div>
								<p className="text-2xl font-bold text-red-900">
									{selectedGroups?.length || 0}
								</p>
							</div>
							<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-orange-700 mb-1">
									<FiUsers size={18} />
									<span className="text-sm font-medium">
										{partial ? "Members Selected" : "Students with Access"}
									</span>
								</div>
								<p className="text-2xl font-bold text-orange-900">
									{partial ? selectedMembers.length : uniqueMemberCount}
								</p>
								{!partial && selectedRevokedMembers.length > 0 && (
									<p className="text-xs text-amber-700 mt-1">
										{selectedRevokedMembers.length} already revoked
									</p>
								)}
							</div>
						</div>
					)}

					<div className="px-6 py-4">
						{currentStep === 0 && (
							<form onSubmit={handleSubmit} className="space-y-6">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Select Subject Groups
									</label>
									<div className="relative">
										<input
											type="text"
											placeholder="Filter subject groups by name..."
											className="w-full h-12 border border-gray-300 rounded-lg pr-4 pl-11 focus:ring-2 focus:ring-red-400 focus:border-transparent"
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
										<span className="absolute left-4 top-1/2 transform -translate-y-1/2">
											{isLoadingSubjectGroups ? (
												<AiOutlineLoading3Quarters
													size={18}
													className="text-gray-400 animate-spin"
												/>
											) : (
												<BsSearch size={18} className="text-gray-400" />
											)}
										</span>
									</div>
								</div>

								<div className="border border-gray-200 rounded-lg overflow-hidden">
									{tableData && tableData.length > 0 ? (
										<DataTable
											value={tableData}
											selection={selectedGroups}
											onSelectionChange={(e) => {
												setSelectedGroups(e.value);
												setFieldValue("subjectGroups", [...e.value]);
												// Drop any selected members that no longer belong to a selected group.
												const memberIds = new Set(
													e.value.flatMap((g) =>
														(g.assignedUsers || []).map((m) => m.id),
													),
												);
												setSelectedMembers((prev) =>
													prev.filter((m) => memberIds.has(m.id)),
												);
											}}
											loading={isLoadingSubjectGroups}
											tableClassName="font-sans text-sm"
											className="w-full"
											autoLayout
										>
											<Column
												selectionMode="multiple"
												headerStyle={{ width: "3em" }}
											></Column>
											<Column field="name" header="Subject Group Name" />
											<Column field="created_by" header="Created by" sortable />
											<Column field="institution_name" header="Institution" sortable />
											<Column
												field="assigned_users_count"
												header="Students"
												sortable
												body={(rowData) => (
													<span className="inline-flex flex-col gap-0.5">
														<span className="inline-flex items-center gap-1">
															<FiUsers size={14} className="text-gray-500" />
															{rowData.activeCount} with access
														</span>
														{rowData.revokedCount > 0 && (
															<span className="text-xs text-amber-700">
																{rowData.revokedCount} already revoked
															</span>
														)}
													</span>
												)}
											/>
										</DataTable>
									) : !isLoadingSubjectGroups ? (
										<div className="py-12 text-center text-gray-500">
											<FiLayers size={48} className="mx-auto mb-3 text-gray-300" />
											<p className="font-medium">No subject groups found</p>
											<p className="text-sm">
												{searchTerm
													? "Try a different search term"
													: "No subject groups have this book assigned"}
											</p>
										</div>
									) : null}
								</div>

								{selectedRevokedMembers.length > 0 && (
									<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
										<p className="text-sm font-medium text-amber-900 mb-2">
											Already revoked from this book ({selectedRevokedMembers.length})
										</p>
										<p className="text-xs text-amber-800 mb-3">
											These students were previously revoked from the selected group(s) but
											remain group members. They cannot be selected again for partial
											revoke.
										</p>
										<ul className="text-sm text-amber-900 space-y-1">
											{selectedRevokedMembers.map((member) => (
												<li key={member.id}>
													• {formatMemberName(member)}
													{member.email ? ` (${member.email})` : ""}
												</li>
											))}
										</ul>
									</div>
								)}

								{/* Partial revoke toggle */}
								<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
									<label className="flex items-center gap-3 cursor-pointer">
										<input
											type="checkbox"
											checked={partial}
											onChange={(e) => {
												setPartial(e.target.checked);
												setSelectedMembers([]);
											}}
											className="w-4 h-4"
										/>
										<span className="text-sm font-medium text-gray-700">
											Revoke from specific students only (instead of the whole group)
										</span>
									</label>

									{partial && (
										<div className="mt-4 space-y-3">
											{selectedGroups.length === 0 ? (
												<p className="text-sm text-gray-500 italic">
													Select one or more subject groups above to choose specific
													members.
												</p>
											) : (
												<>
													<div className="relative">
														<input
															type="text"
															placeholder="Filter students..."
															className="w-full h-11 border border-gray-300 rounded-lg pr-4 pl-11 focus:ring-2 focus:ring-red-400 focus:border-transparent"
															value={memberSearchTerm}
															onChange={(e) => setMemberSearchTerm(e.target.value)}
														/>
														<span className="absolute left-4 top-1/2 transform -translate-y-1/2">
															<BsSearch size={16} className="text-gray-400" />
														</span>
													</div>
													<div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
														{groupMembers && groupMembers.length > 0 ? (
															<DataTable
																value={groupMembers}
																selection={selectedMembers}
																onSelectionChange={(e) => setSelectedMembers(e.value)}
																tableClassName="font-sans text-sm"
																className="w-full"
																autoLayout
															>
																<Column
																	selectionMode="multiple"
																	headerStyle={{ width: "3em" }}
																></Column>
																<Column field="first_name" header="First Name" sortable />
																<Column field="last_name" header="Last Name" sortable />
																<Column field="email" header="Email" sortable />
															</DataTable>
														) : (
															<div className="py-8 text-center text-gray-500 text-sm">
																{selectedRevokedMembers.length > 0
																	? "All remaining students in the selected group(s) have already been revoked from this book."
																	: "No students in the selected group(s)"}
															</div>
														)}
													</div>
													<p className="text-xs text-gray-500 italic">
														Only the selected students from the chosen group(s) will be
														revoked. Other members keep access.
													</p>
												</>
											)}
										</div>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Reason (optional)
									</label>
									<input
										type="text"
										placeholder="e.g. Reassigned to another class"
										className="w-full h-12 border border-gray-300 rounded-lg px-4 focus:ring-2 focus:ring-red-400 focus:border-transparent"
										value={reason}
										onChange={(e) => setReason(e.target.value)}
										maxLength={255}
									/>
								</div>

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={
											isRevoking ||
											selectedGroups.length === 0 ||
											!book ||
											(partial && selectedMembers.length === 0)
										}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										Review Revocation
										<FiArrowRight size={20} />
									</button>
								</div>
							</form>
						)}

						{currentStep === 1 && (
							<div className="space-y-6">
								<div className="bg-red-50 border border-red-200 rounded-lg p-6">
									<div className="flex items-start gap-4">
										<div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
											<FiAlertTriangle size={24} className="text-red-600" />
										</div>
										<div className="flex-1">
											<h4 className="font-medium text-red-900 mb-2">
												Confirm Revocation
											</h4>
											<p className="text-sm text-red-700 mb-4">
												You are about to revoke "{book?.title}" from{" "}
												{selectedGroups.length} subject group
												{selectedGroups.length !== 1 ? "s" : ""}
												{partial
													? ` (only ${selectedMembers.length} selected student${
															selectedMembers.length !== 1 ? "s" : ""
													  } with access)`
													: ` (${uniqueMemberCount} student${
															uniqueMemberCount !== 1 ? "s" : ""
													  } with access${
															selectedRevokedMembers.length > 0
																? `; ${selectedRevokedMembers.length} already revoked`
																: ""
													  })`}
												. Released copies/seats become available to assign to others.
											</p>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
												<div>
													<span className="font-medium text-gray-700">
														Subject Groups:
													</span>
													<ul className="mt-1 space-y-1">
														{selectedGroups.map((group) => {
															const revokedInGroup = (group.assignedUsers || []).filter(
																(member) => member.isRevoked,
															).length;
															const activeInGroup =
																(group.assignedUsers || []).length - revokedInGroup;

															return (
																<li key={group.id} className="text-gray-600">
																	• {group.name} ({activeInGroup} with access
																	{revokedInGroup > 0
																		? `, ${revokedInGroup} already revoked`
																		: ""}
																	)
																</li>
															);
														})}
													</ul>
												</div>
												{selectedRevokedMembers.length > 0 && (
													<div>
														<span className="font-medium text-gray-700">
															Already revoked:
														</span>
														<ul className="mt-1 space-y-1">
															{selectedRevokedMembers.map((member) => (
																<li key={member.id} className="text-amber-700">
																	• {formatMemberName(member)}
																</li>
															))}
														</ul>
													</div>
												)}
												{partial && (
													<div>
														<span className="font-medium text-gray-700">Students:</span>
														<ul className="mt-1 space-y-1">
															{selectedMembers.map((member) => (
																<li key={member.id} className="text-gray-600">
																	• {member.first_name} {member.last_name}
																</li>
															))}
														</ul>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setCurrentStep(0)}
										className="flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										<FiArrowLeft size={20} />
										Back to Selection
									</button>
									<button
										type="button"
										onClick={handleConfirmSubmit}
										disabled={isRevoking}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										{isRevoking ? (
											<>
												<AiOutlineLoading3Quarters size={20} className="animate-spin" />
												Revoking...
											</>
										) : (
											<>
												Confirm Revocation
												<BsTrash size={18} />
											</>
										)}
									</button>
								</div>
							</div>
						)}

						{currentStep === 2 && (
							<div className="text-center space-y-6">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
									<BsCheckCircle size={32} className="text-green-600" />
								</div>
								<div>
									<h4 className="font-medium text-gray-900 mb-2">
										{failureCount > 0 && successCount > 0
											? "Revocation Partially Completed"
											: "Revocation Successful!"}
									</h4>
									<p className="text-sm text-gray-600">
										"{book?.title}" has been revoked for {successCount} student
										{successCount !== 1 ? "s" : ""} across {selectedGroups.length}{" "}
										subject group{selectedGroups.length !== 1 ? "s" : ""}.
										{failureCount > 0 &&
											` ${failureCount} could not be revoked.`}
									</p>
									{failureCount > 0 && (
										<ul className="mt-3 text-sm text-red-600 text-left max-w-md mx-auto space-y-1">
											{revokeResult.failures.map((failure, index) => (
												<li
													key={`${failure.user_id}-${failure.subject_group_id}-${failure.book_id}-${index}`}
												>
													•{" "}
													{failure.user_id
														? `Student #${failure.user_id}`
														: failure.subject_group_id
															? `Group #${failure.subject_group_id}`
															: "Request"}
													: {failure.reason}
												</li>
											))}
										</ul>
									)}
								</div>
								<div className="flex justify-center gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors font-medium"
									>
										Done
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default RevokeSubjectGroups;
